import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Injector, afterNextRender, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Checkbox } from 'primeng/checkbox';
import { Select } from 'primeng/select';
import { ButtonDirective } from 'primeng/button';
import { CdkDragDrop, CdkDrag, CdkDropList, CdkDragHandle, CdkDragPlaceholder, moveItemInArray } from '@angular/cdk/drag-drop';
import { MediaPickerComponent } from '../media-picker/media-picker';
import { SectionCardComponent } from '../section-card/section-card';
import { AttributesEditorComponent } from '../attributes-editor/attributes-editor';
import { VariantEditorComponent } from '../variant-editor/variant-editor';
import { EditorFilesService, type JobStatusResponse } from '../../../features/editor/services/editor-files.service';
import { EditorJobsService } from '../../../features/editor/services/editor-jobs.service';
import { EditorStateService } from '../../../features/editor/services/editor-state.service';
import { AudioTrackRef, FileCategory } from '../../models';
import { deriveNamesMapKey, deriveSlug, generateEntityId, makeUniqueSlug } from '../../utils/slug';
import { slugOptions } from '../../utils/slug-options';
import { cloneDeep } from '../../utils/clone';
import { setRefVariant, type VariantLike, type VariantRefLike } from '../../utils/variant-fallback';

@Component({
	selector: 'app-audio-track-editor',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, InputText, InputNumber, Checkbox, Select, ButtonDirective, CdkDrag, CdkDropList, CdkDragHandle, CdkDragPlaceholder, MediaPickerComponent, SectionCardComponent, AttributesEditorComponent, VariantEditorComponent],
	templateUrl: './audio-track-editor.html',
	styleUrl: './audio-track-editor.scss',
})
export class AudioTrackEditorComponent {
	 
	audioTrack = input.required<AudioTrackRef[]>();
	argKeys = input<string[]>([]);
	sceneSlugs = input<string[]>([]);
	audioTrackChange = output<AudioTrackRef[]>();
	namesMapPopulate = output<{ key: string; names: string[] }>();

	protected sceneOptions(current?: string | null): { label: string; value: string }[] {
		return slugOptions(this.sceneSlugs(), current);
	}

	protected expandedAudio = signal<number | null>(null);
	protected audioPickerVisible = signal(false);
	protected audioPickerCurrentValue = signal('');
	protected readonly audioPickerCategories: FileCategory[] = ['audios'];
	private audioPickerTargetIndex: number | null = null;

	protected addPickerVisible = signal(false);

	private filesService = inject(EditorFilesService);
	private jobsService = inject(EditorJobsService);
	private stateService = inject(EditorStateService);
	private destroyRef = inject(DestroyRef);
	private hostRef = inject(ElementRef<HTMLElement>);
	private injector = inject(Injector);
	protected audioGenerateIndex = signal<number | null>(null);

	/** Forces the "Audio Tracks" section card open when a timeline click requests a row. */
	protected sectionExpandRequest = signal(0);
	private lastHandledAudioExpandVersion = 0;

	constructor() {
		effect(() => {
			const request = this.stateService.audioExpandRequest();
			if (!request || request.version === this.lastHandledAudioExpandVersion) return;
			const index = this.audioTrack().findIndex(a => a.slug === request.slug);
			if (index === -1) return; // data not loaded yet — effect re-runs once audioTrack() updates
			this.lastHandledAudioExpandVersion = request.version;
			this.expandedAudio.set(index);
			this.sectionExpandRequest.update(n => n + 1);
			afterNextRender(() => this.scrollToAudioRow(index), { injector: this.injector });
		});
	}

	private scrollToAudioRow(index: number): void {
		const row = this.hostRef.nativeElement.querySelectorAll('.audio-item')[index];
		row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
	}

	protected audioGenerateStatus(): JobStatusResponse | null {
		const idx = this.audioGenerateIndex();
		if (idx === null) return null;
		const src = this.audioTrack()[idx]?.src;
		if (!src) return null;
		const media = this.filesService.findByBaseUrl(src);
		const mediaId = media?.id;
		if (!mediaId) return null;
		const job = this.jobsService.getJobForMedia(mediaId);
		if (!job) return null;
		return {
			jobId: job.jobId, type: job.type, mediaId: job.mediaId,
			status: job.status, progress: job.progress,
			currentStep: job.currentStep, completedVersions: job.completedVersions,
			totalVersions: job.totalVersions, error: job.error,
		};
	}

	private emit(): void {
		this.audioTrackChange.emit([...this.audioTrack()]);
	}

	 
	private patchAt(index: number, patcher: (audio: any) => void): void {
		const arr = cloneDeep(this.audioTrack());
		if (arr[index]) {
			patcher(arr[index]);
			this.audioTrackChange.emit(arr);
		}
	}

	protected dropAudioTrack(event: CdkDragDrop<unknown[]>): void {
		const arr = [...this.audioTrack()];
		moveItemInArray(arr, event.previousIndex, event.currentIndex);
		this.audioTrackChange.emit(arr);
	}

	protected removeAudioTrack(index: number, event: MouseEvent): void {
		event.stopPropagation();
		if (!confirm('Delete this audio track?')) return;
		const arr = [...this.audioTrack()];
		arr.splice(index, 1);
		this.audioTrackChange.emit(arr);
	}

	protected toggleAudioExpand(index: number, event: MouseEvent): void {
		event.stopPropagation();
		this.expandedAudio.update(current => current === index ? null : index);
	}

	protected setAudioField(index: number, field: string, value: unknown): void {
		this.patchAt(index, audio => {
			if (value === '' || value === null || value === undefined) {
				delete audio[field];
			} else {
				audio[field] = value;
			}
		});
	}

	protected setAudioVariant(index: number, variant: VariantLike | null): void {
		const arr = cloneDeep(this.audioTrack()) as unknown as VariantRefLike[];
		const audio = arr[index];
		if (!audio) return;
		setRefVariant(arr, audio, variant);
		this.audioTrackChange.emit(arr as unknown as AudioTrackRef[]);
	}

	protected setAudioAttributes(index: number, attrs: Record<string, unknown>): void {
		this.patchAt(index, audio => {
			const keys = Object.keys(attrs);
			if (keys.length > 0) {
				audio.attributes = attrs;
				audio.groups = keys;
			} else {
				delete audio.attributes;
				delete audio.groups;
			}
		});
	}

	protected openAudioPicker(index: number): void {
		this.audioPickerTargetIndex = index;
		const audio = this.audioTrack()[index] as { src?: string } | undefined;
		this.audioPickerCurrentValue.set(audio?.src ?? '');
		this.audioPickerVisible.set(true);
	}

	protected onAudioPicked(url: string): void {
		const index = this.audioPickerTargetIndex;
		if (index === null) return;
		this.patchAt(index, audio => {
			audio.src = url;
			audio.srcType = 'ABSOLUTE';
		});
		// Auto-generate versions for local audio
		const media = this.filesService.findByBaseUrl(url);
		const mediaId = media?.id;
		if (media?.isLocal && mediaId && (media.versionCount ?? 0) <= 1) {
			this.filesService.generateVersions(mediaId)
				.pipe(this.jobsService.andTrack({
					mediaId,
					mediaName: media.name ?? '',
					mediaCategory: 'audios',
					destroyRef: this.destroyRef,
				}))
				.subscribe();
		}
	}

	protected isVariantSrc(src: string | undefined): boolean {
		return !!src && src.includes('[VARIANT]');
	}

	protected getAudioSlugs(): string[] {
		return this.audioTrack().map((a: { slug: string }) => a.slug);
	}

	protected isLocalAudioSrc(src: string | undefined): boolean {
		if (!src) return false;
		const media = this.filesService.findByBaseUrl(src);
		return !!media?.isLocal;
	}

	protected hasAudioVersions(src: string | undefined): boolean {
		if (!src) return false;
		const media = this.filesService.findByBaseUrl(src);
		return (media?.versionCount ?? 0) > 1;
	}

	protected startAudioGenerate(index: number, src: string): void {
		const media = this.filesService.findByBaseUrl(src);
		const mediaId = media?.id;
		if (!mediaId) return;
		this.audioGenerateIndex.set(index);
		this.filesService.generateVersions(mediaId)
			.pipe(this.jobsService.andTrack({
				mediaId,
				mediaName: media.name ?? '',
				mediaCategory: media.category,
				destroyRef: this.destroyRef,
			}))
			.subscribe();
	}

	protected onFilesSelected(urls: string[]): void {
		const mediaByUrl = new Map(urls.map(u => [u, this.filesService.findByBaseUrl(u)] as const));
		const arr = cloneDeep(this.audioTrack());
		const existingSlugs = new Set<string>(arr.map(a => a.slug));
		for (const baseUrl of urls) {
			const media = mediaByUrl.get(baseUrl);
			const rawName = media?.name ?? baseUrl.split('/').pop() ?? 'audio';
			const name = rawName.replace(/\.[^.]+$/, '');
			const slug = makeUniqueSlug(deriveSlug(name) || 'audio', existingSlugs);
			existingSlugs.add(slug);
			const entry: AudioTrackRef = { id: generateEntityId(), name, slug, src: baseUrl, srcType: 'ABSOLUTE' };
			if (baseUrl.includes('[VARIANT]')) {
				entry.variant = { type: 'NAME', key: '', namesMapKey: deriveNamesMapKey(media?.name ?? slug) };
			}
			arr.push(entry);
		}
		this.audioTrackChange.emit(arr);

		// Auto-generate for each added local audio/variant group
		for (const media of mediaByUrl.values()) {
			const mediaId = media?.id;
			if (media?.isLocal && mediaId && (media.versionCount ?? 0) <= 1) {
				this.filesService.generateVersions(mediaId)
					.pipe(this.jobsService.andTrack({
						mediaId,
						mediaName: media.name ?? '',
						mediaCategory: 'audios',
						destroyRef: this.destroyRef,
					}))
					.subscribe();
			}
		}
	}
}
